import { useState } from "react";
import { CustomModal } from "@/components/Modal";
import { PageTitle } from "@/components/Title";
import Stack from "@mui/material/Stack";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { Box, Typography } from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import {
  AllMattersAnalyzeOptionsCheckbox,
  type AllMattersAnalyzeOptions,
} from "@/components/AcquireBook/AllMattersAnalyzeOptionsCheckbox";

interface IProps {
  open: boolean;
  handleClose: () => void;
  handleSubmit: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void,
    analysisOptions?: AllMattersAnalyzeOptions
  ) => void;
  type: "owner" | "allMatters";
  timeout?: number;
  isReadOnlyBlocked?: boolean;
}

const defaultAnalysisOptions: AllMattersAnalyzeOptions = {
  includeTitleSection: true,
  includeKoku: true,
  includeOtsuku: true,
  analyzeMethodType: "rule_based",
};

export const ConfirmAnalyzeDialog: React.FC<IProps> = (props: IProps) => {
  const {
    open,
    handleClose,
    handleSubmit,
    type,
    timeout = 3000,
    isReadOnlyBlocked = false,
  } = props;
  const [analysisOptions, setAnalysisOptions] =
    useState<AllMattersAnalyzeOptions>(defaultAnalysisOptions);

  const message =
    type === "owner" ? (
      "所有者事項の解析を行います。"
    ) : (
      <>
        全部事項の解析を行います。
        <br />
        解析する項目を選択してください。
      </>
    );

  const handleDialogClose = (): void => {
    handleClose();
    setAnalysisOptions(defaultAnalysisOptions);
  };

  return (
    <CustomModal
      isOpen={open}
      handleClose={handleDialogClose}
      ariaLabelledby="confirm-analyze-dialog"
      ariaDescribedby="confirm-analyze-dialog-description"
    >
      <Stack direction={"column"} spacing={3}>
        {/* タイトル */}
        <Box id="confirm-analyze-dialog">
          <PageTitle>確認</PageTitle>
        </Box>
        {/* テキスト + 解析オプション */}
        <Box>
          <Typography id="confirm-analyze-dialog-description" variant="body1">
            {message}
          </Typography>
          {type === "allMatters" && (
            <AllMattersAnalyzeOptionsCheckbox
              value={analysisOptions}
              onChange={setAnalysisOptions}
            />
          )}
        </Box>
        {/* 注意書き */}
        <Typography variant="body1">
          「解析する」ボタンを押すと、解析を実行し、結果一覧画面へ移動します。
          <br />
          ※解析が完了するまでお時間がかかる場合がございます。「更新」ボタンを押して最新の結果をご確認ください。
        </Typography>
        {/* ボタン */}
        <Stack
          direction={"row"}
          spacing={2}
          marginTop={2}
          justifyContent={"space-between"}
        >
          <Button
            label="閉じる"
            onClick={handleDialogClose}
            size={SizingWrapperStyle.INHERIT}
            variant={ButtonVariantOption.Outlined}
          />
          <ReadOnlyTooltip isReadOnly={isReadOnlyBlocked}>
            <SubmitButton
              label="解析する"
              onClick={(event, cancelLoading) => {
                handleSubmit(
                  event,
                  cancelLoading,
                  type === "allMatters" ? analysisOptions : undefined
                );
              }}
              size={SizingWrapperStyle.INHERIT}
              variant={ButtonVariantOption.Contained}
              timeout={timeout}
            />
          </ReadOnlyTooltip>
        </Stack>
      </Stack>
    </CustomModal>
  );
};
